// frontend/src/components/settings/LinkSupplierToExactDialog.jsx
//
// Link a Manexys-native supplier (no erp_id) to its Exact account, launched
// per-row from Supplier Setup while an ERP owns supplier master.
//
// The backend returns two candidate buckets (supplier_link.search_link_candidates):
//   * merge  — existing LINKED Manexys suppliers (they mirror Exact accounts).
//              Picking one merges this native row into that Exact identity,
//              absorbing the auto-imported duplicate.
//   * adopt  — live Exact accounts not yet imported (dismissed at connect, or
//              just created in Exact). Picking one stamps the Exact identity
//              onto this native row.
// Either choice POSTs the same link endpoint; the survivor is always the
// native row (it keeps its ItemLocations, PO history, and planning config).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import DialogErrorBanner from '../common/DialogErrorBanner';

import {
  useExactSupplierLinkCandidates,
  useExactLinkSupplier,
} from '../../hooks/useConnectorMutations';
import DialogHeading from '../common/DialogHeading';
import SearchField from '../common/SearchField';
import { translateRequestError } from '../../queries/api';

export default function LinkSupplierToExactDialog({
  connectorId, supplier, open, onClose, onLinked,
}) {
  const { t } = useTranslation();
  const supplierId = supplier.supplier_id;

  // The dialog is conditionally mounted per "Link" click (see SupplierSetup),
  // so each open is a fresh instance — these initial values are the reset.
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [linkError, setLinkError] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  // Debounce the search term feeding the candidates query.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isPending, error } = useExactSupplierLinkCandidates(
    connectorId, supplierId, query, open,
  );
  const linkMut = useExactLinkSupplier(connectorId);

  const handleLink = async (exactAccountId) => {
    setLinkError(null);
    setPendingId(exactAccountId);
    try {
      await linkMut.mutateAsync({
        nativeSupplierId: supplierId, exactAccountId,
      });
      onLinked();
    } catch (err) {
      setLinkError(translateRequestError(err, t, 'connectors.linkSupplierFailed'));
    } finally {
      setPendingId(null);
    }
  };

  const renderRow = (key, primary, secondary, exactAccountId) => (
    <ListItem
      key={key}
      disablePadding
      secondaryAction={(
        <Button
          size="small"
          variant="outlined"
          loading={pendingId === exactAccountId}
          disabled={linkMut.isPending}
          onClick={() => handleLink(exactAccountId)}
        >
          {t('connectors.linkSupplierUse')}
        </Button>
      )}
    >
      <ListItemButton
        disabled={linkMut.isPending}
        onClick={() => handleLink(exactAccountId)}
        sx={{ pr: 12 }}
      >
        <ListItemText primary={primary} secondary={secondary} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Dialog open={open} onClose={onClose} className="dialog-form">
      <DialogTitle>
        <DialogHeading title={t('connectors.linkSupplierTitle')} subtitle={supplier.supplier_name} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {t('connectors.linkSupplierHelp')}
        </Typography>

        <SearchField
          autoFocus
          label={t('supplierSetup.searchLabel')}
          placeholder={t('connectors.linkSupplierSearchPlaceholder')}
          value={searchInput}
          onChange={setSearchInput}
        />

        {error ? (
          <Alert severity="error">{translateRequestError(error, t, 'connectors.resolveLoadFailed')}</Alert>
        ) : isPending ? (
          <div style={{ padding: 16, textAlign: 'center' }}><CircularProgress size={20} /></div>
        ) : (data.merge.length === 0 && data.adopt.length === 0) ? (
          <Alert severity="info">
            {query
              ? t('connectors.linkSupplierNoResults')
              : t('connectors.linkSupplierStartTyping')}
          </Alert>
        ) : (
          <>
            {data.merge.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t('connectors.linkSupplierMergeSection')}
                </Typography>
                <List dense disablePadding>
                  {data.merge.map((c) => renderRow(
                    `m-${c.exact_account_id}`,
                    c.name,
                    [c.erp_code, c.currency].filter(Boolean).join(' · '),
                    c.exact_account_id,
                  ))}
                </List>
              </>
            )}
            {data.adopt.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t('connectors.linkSupplierAdoptSection')}
                </Typography>
                <List dense disablePadding>
                  {data.adopt.map((c) => renderRow(
                    `a-${c.exact_account_id}`,
                    c.name,
                    [c.erp_code, c.currency].filter(Boolean).join(' · '),
                    c.exact_account_id,
                  ))}
                </List>
              </>
            )}
          </>
        )}

        <DialogErrorBanner error={linkError} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={linkMut.isPending}>
          {t('btn.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
